import type { CompareCell, ComparePage } from "./types"

/** Hosts a competitor fact may be sourced from (apex or any subdomain, https only). */
export const SOURCE_DOMAIN_ALLOW_LIST = [
  "driversnote.com.au",
  "apps.apple.com",
  "everlance.com",
  "mileiq.com",
  "gofar.co",
  "crunchr.com.au",
  "instantreceipts.com.au",
  "ato.gov.au",
]

/** 90-day recheck plus 30 days of grace (compare-seo.md section 5). */
export const MAX_SOURCE_AGE_DAYS = 120

const BANNED_PHRASES = /ATO[- ]approved|ATO[- ]endorsed|\bbest\b|#1\b|\bleading\b|\bcheapest\b/i
const BARE_NO = /^(no|none|not (available|offered|supported|included)|does not|doesn't|lacks)\b/i
const DAY_MS = 86_400_000

// Sydney calendar day, so a recheck dated in the morning local time is not "in the future" in UTC
export const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "Australia/Sydney" })

const parseDay = (s: string) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null
  const t = Date.parse(`${s}T00:00:00Z`)
  return Number.isNaN(t) || new Date(t).toISOString().slice(0, 10) !== s ? null : t
}

const hostAllowed = (url: string) => {
  try {
    const u = new URL(url)
    return (
      u.protocol === "https:" &&
      SOURCE_DOMAIN_ALLOW_LIST.some((d) => u.hostname === d || u.hostname.endsWith(`.${d}`))
    )
  } catch {
    return false
  }
}

/** The one date shown as "Last reviewed" and written to dateModified: the newest checkedAt. */
export const lastReviewed = (page: ComparePage) =>
  Object.values(page.sources)
    .map((s) => s.checkedAt)
    .toSorted()
    .at(-1)

/** Returns human-readable problems; empty means the page may ship. `now` is injectable for tests. */
export function validateComparePage(page: ComparePage, now: string): string[] {
  const errors: string[] = []
  const nowMs = parseDay(now)
  if (nowMs === null) throw new Error(`validateComparePage: now "${now}" is not YYYY-MM-DD`)
  if (page.rows.length === 0) errors.push(`${page.slug}: no rows`)

  for (const [id, s] of Object.entries(page.sources)) {
    const at = `${page.slug} / source ${id}`
    if (!hostAllowed(s.url)) errors.push(`${at}: url is not https on the allow-list: ${s.url}`)
    const t = parseDay(s.checkedAt)
    if (t === null) errors.push(`${at}: checkedAt "${s.checkedAt}" is not a YYYY-MM-DD date`)
    else if (t > nowMs) errors.push(`${at}: checkedAt ${s.checkedAt} is in the future`)
    else if ((nowMs - t) / DAY_MS > MAX_SOURCE_AGE_DAYS)
      errors.push(
        `${at}: checkedAt ${s.checkedAt} is ${(nowMs - t) / DAY_MS} days old (max ${MAX_SOURCE_AGE_DAYS})`,
      )
  }

  for (const row of page.rows) {
    const at = `${page.slug} / ${row.label}`
    const cell: CompareCell = row.theirs
    if (cell.sources.length === 0) errors.push(`${at}: competitor cell has no source`)
    for (const id of cell.sources) if (!page.sources[id]) errors.push(`${at}: unknown source ${id}`)
    if (BARE_NO.test(cell.value) && !cell.quote)
      errors.push(
        `${at}: "${cell.value}" about a competitor needs a source quote (write "Not stated on its site" instead)`,
      )
    // quotes are the competitor's own words, so they are not scanned
    for (const text of [row.label, row.ours ?? "", cell.value])
      if (BANNED_PHRASES.test(text)) errors.push(`${at}: banned phrase in "${text}"`)
  }
  return errors
}
